import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  console.log('🔄 CartProvider: Component rendering');
  
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    console.log('🔄 CartContext: useEffect triggered');
    const savedCart = localStorage.getItem('bawarchi-cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        
        // Clean up any items with price objects and ensure valid data
        const cleanedCart = parsedCart.map(item => {
          let itemPrice = 0;
          if (typeof item.price === 'object' && item.price !== null) {
            itemPrice = item.price[item.portion] || item.price.half || 0;
          } else if (typeof item.price === 'number') {
            itemPrice = item.price;
          } else {
            itemPrice = 0;
          }
          
          // Ensure all values are valid
          return {
            ...item,
            price: Number(itemPrice) || 0,
            quantity: Number(item.quantity) || 1,
            id: item.id || '',
            name: item.name || 'Unknown Item',
            portion: item.portion || 'half',
            image: item.image || '',
            category: item.category || 'unknown'
          };
        }).filter(item => item.id && item.name); // Remove invalid items
        
        setCartItems(cleanedCart);
      } catch (error) {
        console.error('Error parsing cart from localStorage:', error);
        // Clear corrupted cart data
        localStorage.removeItem('bawarchi-cart');
        setCartItems([]);
      }
    }
  }, []);

  // Save cart to localStorage whenever cart changes
  useEffect(() => {
    localStorage.setItem('bawarchi-cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item, portion, quantity = 1) => {
    console.log('➕ addToCart called with:', { item, portion, quantity });
    console.log('➕ Current cartItems:', cartItems);
    
    const existingItem = cartItems.find(
      cartItem => cartItem.id === item.id && cartItem.portion === portion
    );

    if (existingItem) {
      console.log('➕ Item exists, updating quantity');
      setCartItems(prevItems =>
        prevItems.map(cartItem =>
          cartItem.id === item.id && cartItem.portion === portion
            ? { ...cartItem, quantity: cartItem.quantity + quantity }
            : cartItem
        )
      );
    } else {
      // Extract numeric price from item.price (could be object or number)
      let itemPrice = 0;
      if (typeof item.price === 'object' && item.price !== null) {
        itemPrice = item.price[portion] || item.price.half || 0;
      } else if (typeof item.price === 'number') {
        itemPrice = item.price;
      } else {
        itemPrice = 0;
      }
      
      // Ensure itemPrice is a valid number
      itemPrice = Number(itemPrice) || 0;
      
      const newItem = {
        id: item.id,
        name: item.name,
        image: item.image,
        portion: portion,
        price: itemPrice,
        quantity: quantity,
        category: item.category
      };
      
      console.log('➕ Adding new item:', newItem);
      
      setCartItems(prevItems => {
        const newCart = [...prevItems, newItem];
        console.log('➕ New cart after adding:', newCart);
        return newCart;
      });
    }

    toast.success(`${item.name} (${portion}) added to cart!`);
  };

  const removeFromCart = (itemId, portion) => {
    setCartItems(prevItems =>
      prevItems.filter(item => !(item.id === itemId && item.portion === portion))
    );
    toast.success('Item removed from cart!');
  };

  const updateQuantity = (itemId, portion, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId, portion);
      return;
    }

    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId && item.portion === portion
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    setAppliedCoupon(null);
    toast.success('Cart cleared!');
  };

  const clearCartSilent = () => {
    setCartItems([]);
    setAppliedCoupon(null);
    // No toast notification for silent clearing
  };


  const getCartTotal = () => {
    console.log('🛒 getCartTotal called with cartItems:', cartItems);
    const total = cartItems.reduce((total, item) => {
      let itemPrice = 0;
      if (typeof item.price === 'object' && item.price !== null) {
        itemPrice = item.price[item.portion] || item.price.half || 0;
      } else if (typeof item.price === 'number') {
        itemPrice = item.price;
      } else {
        itemPrice = 0;
      }
      
      // Ensure both itemPrice and quantity are valid numbers
      itemPrice = Number(itemPrice) || 0;
      const quantity = Number(item.quantity) || 0;
      
      console.log(`💰 Item: ${item.name}, Price: ${itemPrice}, Quantity: ${quantity}, Subtotal: ${itemPrice * quantity}`);
      return total + (itemPrice * quantity);
    }, 0);
    console.log('🛒 Final cart total:', total);
    return total;
  };

  const getCartDiscount = () => {
    if (!appliedCoupon) return 0;
    
    const subtotal = getCartTotal();
    let discount = 0;

    if (appliedCoupon.type === 'percentage') {
      discount = (subtotal * appliedCoupon.value) / 100;
      if (appliedCoupon.maxDiscount) {
        discount = Math.min(discount, appliedCoupon.maxDiscount);
      }
    } else if (appliedCoupon.type === 'fixed') {
      discount = appliedCoupon.value;
    }

    return Math.min(discount, subtotal);
  };

  const getFinalTotal = () => {
    return getCartTotal() - getCartDiscount();
  };

  const getCartItemCount = () => {
    console.log('🔢 getCartItemCount called with cartItems:', cartItems);
    const count = cartItems.reduce((total, item) => {
      const quantity = Number(item.quantity) || 0;
      console.log(`🔢 Item: ${item.name}, Quantity: ${quantity}`);
      return total + quantity;
    }, 0);
    console.log('🔢 Final cart item count:', count);
    return count;
  };

  const applyCoupon = (coupon) => {
    const subtotal = getCartTotal();
    
    if (coupon.minOrder && subtotal < coupon.minOrder) {
      toast.error(`Minimum order amount is ${coupon.minOrder}`);
      return false;
    }

    setAppliedCoupon(coupon);
    toast.success(`Coupon "${coupon.code}" applied successfully!`);
    return true;
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    toast.success('Coupon removed!');
  };

  const value = {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    appliedCoupon,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    clearCartSilent,
    getCartTotal,
    getCartDiscount,
    getFinalTotal,
    getCartItemCount,
    applyCoupon,
    removeCoupon
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
